import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowLeft, User, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { usePartner } from '../lib/partnerContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AuthPage() {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  const [searchParams] = useSearchParams();
  const partner = usePartner(searchParams.toString());
  
  const [mode, setMode] = useState('register'); // 'login', 'register', 'forgot' - DEFAULT TO REGISTER
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  // Form data
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  
  // Forgot password
  const [forgotEmailSent, setForgotEmailSent] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    // Detect if admin is trying to login with admin code on client page
    if (password === 'MARCHAND2024PRO') {
      toast.error('Pour vous connecter en tant qu\'administrateur, utilisez l\'Espace Pro', {
        description: 'Cliquez sur "Espace Pro (Admin)" ci-dessous',
        duration: 6000
      });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/client/login`, { email, password });
      login(response.data.access_token, response.data.user);
      toast.success(`Bienvenue ${response.data.user.name || ''} !`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email et mot de passe requis');
      return;
    }
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/client/register`, { 
        email, 
        password, 
        name: name || null,
        phone: phone || null,
        ...(partner.subdomain && { partner_subdomain: partner.subdomain })
      });
      // Don't auto-login - show success message instead
      setRegistrationSuccess(true);
      toast.success('Compte créé ! Vérifiez votre email (et vos spams).');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Veuillez entrer votre email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/client/forgot-password`, { email, ...(partner.subdomain && { partner_subdomain: partner.subdomain }) });
      setForgotEmailSent(true);
      toast.success('Nouveau mot de passe envoyé ! Vérifiez vos spams si besoin.');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy via-navy/95 to-navy flex items-center justify-center p-4" style={partner.color ? { backgroundImage: 'none', backgroundColor: partner.color } : undefined} data-testid="auth-page">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gold/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(partner.home)}
          className="mb-4 text-white/70 hover:text-white hover:bg-white/10"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'accueil
        </Button>

        <Card className="border-0 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4">
              {partner.isPartner && partner.logo ? (
                <img src={partner.logo} alt={partner.name || 'Partenaire'} className="w-16 h-16 rounded-2xl object-cover mx-auto" data-testid="auth-partner-logo" />
              ) : (
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto" style={{ backgroundColor: partner.color || '#0A192F' }}>
                <span className="text-gold text-2xl font-bold">{partner.isPartner && partner.name ? partner.name.trim().charAt(0).toUpperCase() : 'ŒM'}</span>
              </div>
              )}
            </div>
            <CardTitle className="text-2xl font-bold text-navy">
              {mode === 'login' && 'Connexion'}
              {mode === 'register' && !registrationSuccess && 'Créer un compte'}
              {mode === 'register' && registrationSuccess && 'Inscription réussie'}
              {mode === 'forgot' && 'Mot de passe oublié'}
            </CardTitle>
            <p className="text-muted-foreground text-sm mt-1">
              {mode === 'login' && 'Accédez à votre espace client'}
              {mode === 'register' && !registrationSuccess && (partner.isPartner && partner.name ? `Rejoignez ${partner.name}` : 'Rejoignez L\'Œil du Marchand')}
              {mode === 'register' && registrationSuccess && 'Vérifiez votre email'}
              {mode === 'forgot' && 'Recevez un nouveau mot de passe'}
            </p>
          </CardHeader>

          <CardContent className="space-y-4 pt-4">
            {/* LOGIN FORM */}
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="votre@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy"
                      data-testid="toggle-password-btn"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setMode('forgot')}
                  className="text-sm text-gold hover:underline"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </button>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gold hover:bg-gold/90 text-black font-semibold"
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>

                <div className="text-center">
                  <span className="text-muted-foreground text-sm">Pas encore de compte ? </span>
                  <button
                    type="button"
                    onClick={() => setMode('register')}
                    className="text-sm text-navy font-semibold hover:underline"
                    data-testid="go-to-register-btn"
                  >
                    S'inscrire
                  </button>
                </div>
              </form>
            )}

            {/* REGISTER FORM */}
            {mode === 'register' && !registrationSuccess && (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom (optionnel)</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Jean Dupont"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10"
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reg-email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone (optionnel)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+33 6 12 34 56 78"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="pl-10"
                      data-testid="register-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reg-password">Mot de passe * (min. 6 caractères)</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="reg-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gold hover:bg-gold/90 text-black font-semibold"
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Création...' : 'Créer mon compte'}
                </Button>

                <div className="text-center">
                  <span className="text-muted-foreground text-sm">Déjà un compte ? </span>
                  <button
                    type="button"
                    onClick={() => setMode('login')}
                    className="text-sm text-navy font-semibold hover:underline"
                    data-testid="go-to-login-btn"
                  >
                    Se connecter
                  </button>
                </div>
              </form>
            )}

            {/* REGISTRATION SUCCESS */}
            {mode === 'register' && registrationSuccess && (
              <div className="space-y-4 text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-xl text-emerald-700">Compte créé avec succès !</h3>
                <p className="text-muted-foreground">
                  Un email de confirmation a été envoyé à<br />
                  <strong className="text-navy">{email}</strong>
                </p>
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-left">
                  <p className="text-sm text-emerald-800 mb-2">
                    📧 <strong>Vérifiez votre boîte mail</strong> pour retrouver vos identifiants de connexion.
                  </p>
                  <p className="text-xs text-emerald-600">
                    Pensez à vérifier votre dossier <strong>SPAM</strong> si vous ne le trouvez pas.
                  </p>
                </div>
                <Button
                  onClick={() => { setMode('login'); setRegistrationSuccess(false); setPassword(''); }}
                  className="w-full bg-navy hover:bg-navy/90"
                  data-testid="go-to-login-after-register-btn"
                >
                  Se connecter maintenant
                </Button>
              </div>
            )}

            {/* FORGOT PASSWORD FORM */}
            {mode === 'forgot' && !forgotEmailSent && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Entrez votre email, nous vous enverrons un nouveau mot de passe.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gold hover:bg-gold/90 text-black font-semibold"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Envoi...' : 'Envoyer un nouveau mot de passe'}
                </Button>

                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="w-full text-sm text-muted-foreground hover:text-navy"
                >
                  ← Retour à la connexion
                </button>
              </form>
            )}

            {/* FORGOT PASSWORD SUCCESS */}
            {mode === 'forgot' && forgotEmailSent && (
              <div className="space-y-4 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-lg">Email envoyé !</h3>
                <p className="text-sm text-muted-foreground">
                  Un nouveau mot de passe a été envoyé à <strong>{email}</strong>.
                </p>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-left">
                  <p className="text-sm text-amber-800">
                    📧 Vérifiez votre dossier <strong>SPAM</strong> si vous ne le recevez pas.
                  </p>
                </div>
                <Button
                  onClick={() => { setMode('login'); setForgotEmailSent(false); }}
                  className="w-full bg-navy hover:bg-navy/90"
                >
                  Retour à la connexion
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Links to other portals (masqués en marque blanche) */}
        {!partner.isPartner && (
        <div className="mt-6 text-center space-y-2">
          <button
            onClick={() => navigate('/analyst')}
            className="text-blue-400 text-sm hover:underline block mx-auto"
          >
            Espace Analyste
          </button>
          <button
            onClick={() => navigate('/admin')}
            className="text-gold text-sm hover:underline block mx-auto"
          >
            Espace Pro (Admin)
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
